/**
 * ============================================================================
 *  BRAND / WHITE-LABEL CONFIGURATION — single source of truth
 * ============================================================================
 *
 * Every customer-specific value lives here. To stand up a new white-label
 * instance, edit the values in this file (and replace the referenced asset
 * files on disk) — see WHITE-LABEL-SETUP.md for the full checklist.
 *
 * This module is safe to import from BOTH client and server code: it contains
 * only plain constants, no environment access and no server-only imports.
 *
 * NOTE ON COLOURS: the runtime colour palette is defined as CSS custom
 * properties (design tokens) in `src/styles.css` — that remains the source of
 * truth the UI reads from. The `colors` block below mirrors those values so
 * non-CSS consumers (PWA manifest theme colour, native splash, JSON-LD, etc.)
 * and this setup documentation stay in sync. When rebranding, update BOTH the
 * tokens in `src/styles.css` and the mirror here.
 */

export const brand = {
  // ── Identity ──────────────────────────────────────────────────────────
  /** Public-facing display name. */
  name: "Digitally Baffled",
  /** Registered legal entity name (used in footers, legal pages, JSON-LD). */
  legalName: "Digitally Baffled LTD",
  /** Companies House / registration number (leave "" if not applicable). */
  companyNumber: "17238376",
  /** Short positioning line shown alongside the name. */
  tagline: "UK Digital Transformation Consultancy",
  /** One-line description used as an SEO / social default. */
  description:
    "The digital transformation agency for small businesses who want results without the jargon.",
  /** PWA / installed-app name. */
  appName: "Digitally Baffled",
  /** Description shown for the installed client-portal PWA. */
  portalDescription:
    "Your Digitally Baffled client portal — messages, invoices, agreements and project progress.",

  // ── Marketing voice ───────────────────────────────────────────────────
  voice: {
    footerBlurb:
      "The digital transformation agency for small businesses who want results without the jargon.",
    footerTagline: "Don't get baffled, get growing.",
  },

  // ── Domains & origins ─────────────────────────────────────────────────
  /** Bare apex domain (no scheme). */
  domain: "digitallybaffled.com",
  /** Canonical production URL (no trailing slash). Used for SEO/canonical/OG. */
  url: "https://digitallybaffled.com",
  /** www variant. */
  wwwUrl: "https://www.digitallybaffled.com",
  /** Lovable-hosted staging URL. */
  lovableAppUrl: "https://digitallybaffled.lovable.app",
  /**
   * Exact origins the app is served from and may safely link back to in
   * outbound emails / notifications. Add every custom domain you connect.
   */
  allowedOrigins: [
    "https://digitallybaffled.com",
    "https://www.digitallybaffled.com",
    "https://digitallybaffled.lovable.app",
  ] as const,

  // ── Contact details ───────────────────────────────────────────────────
  contact: {
    email: "hello@digitallybaffled.com",
    /** Human-readable phone number. */
    phoneDisplay: "07511 683542",
    /** E.164 phone number for tel: / wa.me links. */
    phoneE164: "+447511683542",
    /** Postal address as separate lines. */
    addressLines: ["18 Charing Close", "Ringwood", "BH24 1FA", "United Kingdom"],
    /** Postal address as a single inline string. */
    addressInline: "18 Charing Close, Ringwood, BH24 1FA, United Kingdom",
    /** Region shown in copy ("based in …"). */
    region: "Ringwood, Hampshire",
    /** Availability note shown next to contact links. */
    availability: "available 24/7",
  },

  // ── System administrator ──────────────────────────────────────────────
  /**
   * Email of the bootstrap "system admin" account. This address is auto-granted
   * the admin role on sign-up and has elevated privileges (delete accounts /
   * enquiries, appoint other admins). Change this per customer BEFORE the first
   * account is created, and update the DB `handle_new_user` / `is_system_admin`
   * functions to match (see WHITE-LABEL-SETUP.md).
   */
  systemAdminEmail: "morganantell@gmail.com",

  // ── Social profiles ───────────────────────────────────────────────────
  social: {
    linkedin: "https://www.linkedin.com/company/119624069",
    instagram: "https://www.instagram.com/digitally_baffled",
    instagramHandle: "@digitally_baffled",
    facebook: "https://www.facebook.com/share/1D8QbXPZuP/",
    tiktok: "https://www.tiktok.com/@digitally_baffled",
    tiktokHandle: "@digitally_baffled",
    /**
     * Google Business Profile URL (your public profile / reviews page).
     * PLACEHOLDER — paste the real URL here to surface it in the JSON-LD
     * `sameAs` arrays and as a footer link. While it's "" it stays hidden.
     */
    googleBusinessProfile: "",
  },

  // ── Google reviews / rating badge ─────────────────────────────────────
  /**
   * Controls the "Google Reviews" rating badge shown near the testimonials.
   *
   * TO TURN IT ON:
   *   1. Paste your Google Business profile / reviews URL into `profileUrl`.
   *   2. Set `rating` and `reviewCount` to your real numbers.
   *   3. Flip `enabled` to true.
   * While `enabled` is false (or `profileUrl` is empty) the badge stays hidden.
   */
  googleReviews: {
    /** Master on/off switch — keep false until the URL below is filled in. */
    enabled: false,
    /** Full URL to your Google Business profile / reviews page. */
    profileUrl: "",
    /** Average star rating to display (e.g. 5 or 4.9). */
    rating: 5,
    /** Total number of Google reviews to display. */
    reviewCount: 0,
  },

  // ── Brand assets (replace the files on disk when rebranding) ──────────
  assets: {
    /** Favicon + touch icons live in /public. */
    favicon: "/favicon.ico",
    faviconPng: "/favicon.png",
    favicon48: "/favicon-48x48.png",
    favicon96: "/favicon-96x96.png",
    appleTouchIcon: "/apple-touch-icon.png",
    /** PWA icons. */
    icon192: "/icons/icon-192.png",
    icon512: "/icons/icon-512.png",
    /** Absolute URLs used in OG tags / JSON-LD. */
    ogImage:
      "https://digitallybaffled.com/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png",
    headerLogo:
      "https://digitallybaffled.com/__l5e/assets-v1/f0bdc5e9-161d-4eba-9b27-66e9f15d47b0/site-header-logo.png",
  },

  // ── Colour palette (mirror of src/styles.css design tokens) ──────────
  colors: {
    /** Primary brand colour — HSL channels ("H S% L%") for CSS vars. */
    primary: "243 75% 59%",
    /** Primary as hex (manifest theme, native config, JSON-LD). */
    primaryHex: "#5048E5",
    /** Secondary / coral accent — HSL channels. */
    secondary: "14 100% 57%",
    secondaryHex: "#ff5a2c",
    /** Gradient end used in headline accents. */
    gradientEndHex: "#a855f7",
    /** App background (splash / manifest background_color). */
    backgroundHex: "#0b0b13",
    /** Native splash / icon background. */
    nativeBackgroundHex: "#0b0b12",
    /** PWA theme colour. */
    themeColorHex: "#5048E5",
  },
} as const;

/** Full brand title, e.g. "Digitally Baffled LTD — UK Digital Transformation Consultancy". */
export const brandFullTitle = `${brand.legalName} — ${brand.tagline}`;

/**
 * Public social / directory profiles, in one place, surfaced BOTH in the
 * JSON-LD `sameAs` arrays (src/routes/__root.tsx) and as footer links.
 * Each entry only appears once its `url` is non-empty — so the Google Business
 * Profile stays hidden until you fill in `brand.social.googleBusinessProfile`.
 * Never invent URLs here; edit the placeholders in `brand.social`.
 */
export const socialProfiles = [
  { key: "linkedin", label: "LinkedIn", url: brand.social.linkedin },
  { key: "instagram", label: "Instagram", url: brand.social.instagram },
  { key: "tiktok", label: "TikTok", url: brand.social.tiktok },
  { key: "facebook", label: "Facebook", url: brand.social.facebook },
  {
    key: "google",
    label: "Google Business Profile",
    url: brand.social.googleBusinessProfile,
  },
].filter((p) => p.url.trim() !== "");

/** URLs for JSON-LD `sameAs`. Empty placeholders are filtered out above. */
export const sameAsUrls = socialProfiles.map((p) => p.url);

export type Brand = typeof brand;

export default brand;
